<template>
  <div class="client-onboarding-page">
    <div class="container py-2">
      <!-- Loading -->
      <div v-if="loading" class="text-center my-5">
        <b-spinner class="align-middle mr-1" />
        <strong>Loading…</strong>
      </div>

      <!-- Invalid link -->
      <b-card v-else-if="notFound" class="text-center hero-card">
        <img src="/logo.png" alt="Swiss Finance Corporation" class="hero-logo mb-2" />
        <feather-icon icon="AlertTriangleIcon" size="48" class="text-warning mb-1" />
        <h3>This link is not valid</h3>
        <p class="text-muted mb-0">
          The onboarding link you followed is invalid or has been revoked.
          Please contact Swiss Finance Corporation for a new link.
        </p>
      </b-card>

      <!-- Access frozen -->
      <b-card v-else-if="frozen" class="text-center hero-card">
        <img src="/logo.png" alt="Swiss Finance Corporation" class="hero-logo mb-2" />
        <feather-icon icon="PauseCircleIcon" size="48" class="text-warning mb-1" />
        <h3>Access paused</h3>
        <p class="text-muted mb-0">
          Access to this onboarding has been paused by Swiss Finance Corporation.
          Please contact your usual SFC contact if you believe this is a mistake.
        </p>
      </b-card>

      <!-- PIN gate -->
      <b-card v-else-if="pinRequired" class="text-center hero-card pin-card">
        <img src="/logo.png" alt="Swiss Finance Corporation" class="hero-logo mb-2" />
        <feather-icon icon="LockIcon" size="48" class="text-primary mb-1" />
        <h3>Enter your PIN</h3>
        <p class="text-muted">
          The onboarding for <strong>{{ accountName }}</strong> is protected by the PIN that was
          emailed to you together with this link.
        </p>
        <b-form class="pin-form mx-auto" @submit.prevent="verifyPin">
          <b-form-input
            v-model="pin"
            type="text"
            inputmode="numeric"
            maxlength="6"
            autocomplete="one-time-code"
            placeholder="6-digit PIN"
            class="text-center pin-input mb-1"
            :state="pinError ? false : null"
            :disabled="pinVerifying || !!pinLockedUntil"
            autofocus
          />
          <b-alert :show="!!pinError" variant="danger" class="p-50 text-left">{{ pinError }}</b-alert>
          <b-button type="submit" variant="primary" block :disabled="pinVerifying || pin.length < 4 || !!pinLockedUntil">
            <b-spinner v-if="pinVerifying" small class="mr-25" />
            Continue
          </b-button>
        </b-form>
        <p class="text-muted small mt-1 mb-0">
          Lost the PIN? Ask your SFC contact to re-send the link; a new PIN comes with it.
        </p>
      </b-card>

      <template v-else>
        <!-- Header -->
        <div class="hero-card card mb-2">
          <div class="hero-accent"></div>
          <div class="card-body text-center">
            <img src="/logo.png" alt="Swiss Finance Corporation" class="hero-logo mb-1" />
            <h3 class="hero-title mb-2">Professional Client Agreement</h3>
            <div class="hero-chips">
              <span class="hero-chip">
                <feather-icon icon="BriefcaseIcon" size="14" class="mr-25" />
                {{ accountName }}<span v-if="accountNumber" class="chip-muted">&nbsp;· {{ accountNumber }}</span>
              </span>
              <span class="hero-chip">
                <feather-icon icon="UserIcon" size="14" class="mr-25" />
                {{ contactName }}
              </span>
              <span class="hero-chip" :class="statusChip.cls">
                <feather-icon :icon="statusChip.icon" size="14" class="mr-25" />
                {{ statusChip.label }}
              </span>
            </div>

            <!-- step strip: the wizard's own tab bar is hidden, this drives it -->
            <nav v-if="wizardReady" class="hero-steps" aria-label="Form steps">
              <button
                v-for="(step, index) in summarySteps"
                :key="'hs' + step.key"
                type="button"
                class="hero-step"
                :class="['hero-step-' + step.state, { 'is-active': index === activeStepIndex, 'is-reachable': index <= maxVisited }]"
                :disabled="index > maxVisited"
                @click="goToStep(index)"
              >
                <span class="hero-step-num">
                  <feather-icon v-if="step.state === 'signed' || step.state === 'done'" icon="CheckIcon" size="12" />
                  <template v-else>{{ index + 1 }}</template>
                </span>
                <span class="hero-step-title">{{ step.title }}</span>
              </button>
            </nav>
          </div>
        </div>

        <b-alert v-if="complete" show variant="success" class="p-1">
          <feather-icon icon="AwardIcon" class="mr-50" />
          Your onboarding is complete. Thank you. You can still view and download your documents on the Documents step.
        </b-alert>
        <b-alert v-else-if="signed && !reopened" show variant="success" class="p-1">
          <feather-icon icon="CheckCircleIcon" class="mr-50" />
          This form was signed by <strong>{{ signatureName }}</strong> on <strong>{{ signedDateLabel }}</strong>.
          The form can no longer be changed, but you can still upload and view documents on the Documents step
          and on the person steps.
        </b-alert>
        <b-alert v-else-if="reopened" show variant="warning" class="p-1">
          <feather-icon icon="RefreshCwIcon" class="mr-50" />
          Swiss Finance Corporation has added new sections or questions to your agreement<template v-if="pendingSectionTitles.length">:
          <strong>{{ pendingSectionTitles.join(", ") }}</strong></template>.
          The sections you signed on <strong>{{ signedDateLabel }}</strong> are shown read-only. Please complete the
          new ones and sign the form again on the final step.
        </b-alert>

        <b-alert :show="!!errorMessage" variant="danger" dismissible class="p-1" @dismissed="errorMessage = null">
          {{ errorMessage }}
        </b-alert>

        <b-row class="onboarding-layout">
          <b-col cols="12" lg="4" xl="3" class="summary-col mb-2">
            <onboarding-summary-card
              :summary="summary"
              :status="status"
              :steps="summarySteps"
              :active-index="activeStepIndex"
              :max-step="maxVisited"
              @go="goToStep"
            />
          </b-col>

          <b-col cols="12" lg="8" xl="9">
            <div ref="wizardWrap" class="pca-form">
              <form-wizard
                v-if="wizardReady"
                :key="wizardKey"
                ref="wizard"
                :title="null"
                :subtitle="null"
                color="#2f3a52"
                error-color="#ea5455"
                step-size="xs"
                :start-index="startIndex"
                class="onboarding-wizard"
                @on-change="onStepChange"
              >
                <template #title><span class="d-none"></span></template>

                <tab-content
                  v-for="(step, index) in steps"
                  :key="step.key"
                  :title="step.title"
                  :before-change="() => beforeLeave(index)"
                >
                  <template #default="{ active }">
                    <div class="section-head">
                      <span class="section-letter" :class="{ 'section-letter-icon': step.icon, 'section-letter-sign': step.type === 'review' }">
                        <feather-icon v-if="step.icon" :icon="step.icon" size="16" />
                        <template v-else>{{ step.letter }}</template>
                      </span>
                      <div class="flex-grow-1">
                        <h4 class="mb-0">{{ step.title }}</h4>
                        <small class="text-muted">{{ step.subtitle }}</small>
                      </div>
                      <b-badge v-if="step.type !== 'documents' && step.type !== 'review' && sectionReadOnly(step.key)" variant="light-secondary">
                        <feather-icon icon="LockIcon" size="12" class="mr-25" />
                        Signed
                      </b-badge>
                    </div>

                    <div class="section-body">
                      <b-alert
                        v-if="reopened && step.type !== 'documents' && step.type !== 'review' && sectionReadOnly(step.key)"
                        show
                        variant="secondary"
                        class="p-75"
                      >
                        <small>
                          This section is covered by your signature of {{ signedDateLabel }} and can no longer be changed.
                          <template v-if="step.type === 'persons'">Documents for these persons can still be uploaded.</template>
                        </small>
                      </b-alert>

                      <validation-observer :ref="'observer_' + step.key" tag="div">
                        <dynamic-section
                          v-if="step.type === 'section'"
                          :ref="'section_' + step.key"
                          :section="step.section"
                          :form="form"
                          :disabled="sectionReadOnly(step.key)"
                          :show-hints="showAckHints"
                          :active="active"
                          :notice-signatures="noticeSignatures"
                          :locked-keys="clientTypeLocked ? ['sectionA.clientType'] : []"
                        />

                        <person-list-section
                          v-else-if="step.type === 'persons'"
                          :ref="'persons_' + step.key"
                          :config="step.config"
                          :section="step.section"
                          :value="form.persons[step.config.formKey] || []"
                          :form="form"
                          :requirements="requirements"
                          :disabled="sectionReadOnly(step.key)"
                          :can-upload="true"
                          :active="active"
                          @input="setPersons(step.config.formKey, $event)"
                          @request-save="autosave('background')"
                          @payload="applyPayload"
                        />

                        <documents-page
                          v-else-if="step.type === 'documents'"
                          :requirements="requirements"
                          :documents="documents"
                          :customer-requests="customerRequests"
                          :can-upload="true"
                          @payload="applyPayload"
                        />

                        <div v-else-if="step.type === 'review'">
                          <div class="review-summary mb-2">
                            <h6 class="mt-0">Summary</h6>
                            <b-row class="review-facts">
                              <b-col cols="6" md="3">
                                <small class="text-muted d-block">Account</small>
                                <strong>{{ (form.sectionA && form.sectionA.accountName) || accountName }}</strong>
                              </b-col>
                              <b-col cols="6" md="3">
                                <small class="text-muted d-block">Client type</small>
                                <strong>{{ (form.sectionA && form.sectionA.clientType) || clientType || "—" }}</strong>
                              </b-col>
                              <b-col v-if="hasRecat" cols="6" md="3">
                                <small class="text-muted d-block">Requested classification</small>
                                <strong>{{ (form.sectionF && form.sectionF.classification) || "—" }}</strong>
                              </b-col>
                              <b-col cols="6" md="3">
                                <small class="text-muted d-block">Documents outstanding</small>
                                <strong>{{ summary ? summary.outstanding || 0 : 0 }}</strong>
                              </b-col>
                            </b-row>

                            <ul class="review-checklist mt-1">
                              <li v-for="item in reviewChecklist" :key="'rv' + item.key" :class="'review-' + item.state">
                                <feather-icon :icon="item.icon" size="14" class="mr-50" />
                                <span class="flex-grow-1">{{ item.title }}</span>
                                <small class="text-muted">{{ item.note }}</small>
                              </li>
                            </ul>

                            <p v-if="!formLocked" class="text-muted small mb-0">
                              Please check the information above. Use the step list or the "Previous" button to go back
                              and make changes before signing.
                            </p>
                          </div>

                          <dynamic-section
                            v-if="reviewExtras"
                            ref="section_E"
                            :section="reviewExtras"
                            :form="form"
                            :disabled="formLocked"
                            :show-hints="showAckHints"
                            :active="active"
                            :notice-signatures="noticeSignatures"
                            class="mb-2"
                          />

                          <section-declaration
                            ref="review"
                            :section-e="form.sectionE"
                            :section-f="form.sectionF || null"
                            :declaration="declarationItem"
                            :has-recat="hasRecat"
                            :disabled="formLocked"
                            :active="active"
                            :signed-name="signatureName"
                            :signed-date="signedDateLabel"
                          />
                        </div>
                      </validation-observer>
                    </div>
                  </template>
                </tab-content>

                <template #footer="props">
                  <div class="wizard-footer-left">
                    <b-button v-if="props.activeTabIndex > 0" variant="outline-secondary" :disabled="saving || signing" @click="props.prevTab()">
                      <feather-icon icon="ChevronLeftIcon" size="14" class="mr-25" />
                      Previous
                    </b-button>
                  </div>
                  <div class="wizard-footer-right">
                    <b-button v-if="!formLocked" variant="outline-primary" :disabled="saving || signing" @click="saveProgress">
                      <b-spinner v-if="saving" small class="mr-25" />
                      <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                      Save progress
                    </b-button>
                    <b-button v-if="!props.isLastStep" variant="primary" class="btn-next" :disabled="saving || signing" @click="props.nextTab()">
                      Next
                      <feather-icon icon="ChevronRightIcon" size="14" class="ml-25" />
                    </b-button>
                    <b-button v-else-if="!formLocked" variant="success" class="btn-sign" :disabled="saving || signing" @click="signAndSubmit">
                      <b-spinner v-if="signing" small class="mr-25" />
                      <feather-icon v-else icon="PenToolIcon" size="14" class="mr-25" />
                      Sign &amp; Submit
                    </b-button>
                  </div>
                </template>
              </form-wizard>

              <p v-if="!formLocked" class="text-muted small mt-1 mb-0 text-center">
                Your progress is saved automatically each time you move to the next step. You can come back later using
                the same link. Once signed, the form can no longer be changed.
              </p>
            </div>
          </b-col>
        </b-row>

        <div class="page-footer text-center">
          <img src="/logo.png" alt="Swiss Finance Corporation" class="footer-logo mb-50" />
          <p class="text-muted small mb-0">
            Swiss Finance Corporation Limited — 33 Cavendish Square, London W1G 0PW, United Kingdom<br />
            Authorised and regulated by the Financial Conduct Authority
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import { ValidationObserver } from "vee-validate";
import {
  publicAxios,
  setToken,
  getStatus,
  verifyPin as apiVerifyPin,
  getOnboarding,
  saveForm as apiSaveForm,
  signForm as apiSignForm,
  errorMessage,
  formatDate,
} from "./publicApi";
import { normaliseForm, emptyFormFromSchema, validateForSign } from "./formSchema";
import { personConfig } from "./personTypes";
import OnboardingSummaryCard from "./OnboardingSummaryCard.vue";
import PersonListSection from "./PersonListSection.vue";
import DynamicSection from "./DynamicSection.vue";
import DocumentsPage from "./DocumentsPage.vue";
import SectionDeclaration from "./sections/SectionDeclaration.vue";

const STATUS_CHIPS = {
  Draft: { label: "Draft", cls: "chip-pending", icon: "Edit3Icon" },
  InProgress: { label: "In progress", cls: "chip-pending", icon: "Edit3Icon" },
  Signed: { label: "Signed", cls: "chip-success", icon: "CheckCircleIcon" },
  Reopened: { label: "Reopened — signature required", cls: "chip-warning", icon: "RefreshCwIcon" },
  Complete: { label: "Complete", cls: "chip-success", icon: "AwardIcon" },
};

export default {
  name: "ClientOnboarding",

  components: {
    FormWizard,
    TabContent,
    ValidationObserver,
    OnboardingSummaryCard,
    PersonListSection,
    DynamicSection,
    DocumentsPage,
    SectionDeclaration,
  },

  data() {
    return {
      loading: true,
      notFound: false,
      frozen: false,
      pinRequired: false,
      pin: "",
      pinError: null,
      pinVerifying: false,
      pinLockedUntil: null,

      saving: false,
      savePromise: null,
      signing: false,
      errorMessage: null,
      showAckHints: false,

      // payload
      contactName: "",
      accountName: "",
      accountNumber: "",
      status: "Draft",
      signed: false,
      reopened: false,
      complete: false,
      signedDate: null,
      signatureName: null,
      signedSections: [],
      clientType: null,
      clientTypeLocked: false,
      clientCategory: null,
      form: emptyFormFromSchema(null),
      formSchema: { sections: [] },
      formSections: [],
      noticeSignatures: {},
      personTypes: {},
      persons: [],
      requirements: [],
      customerRequests: [],
      documents: [],
      summary: null,

      // wizard
      wizardReady: false,
      wizardKey: 0,
      wizardFingerprint: null,
      activeStepIndex: 0,
      maxVisited: 0,
    };
  },

  computed: {
    token() {
      return this.$route.params.token;
    },

    // Signed (and not reopened) or complete: nothing on the form may change any more.
    formLocked() {
      return this.complete || (this.signed && !this.reopened);
    },

    sectionKeys() {
      return this.schemaSections.map((section) => section.key);
    },

    schemaSections() {
      return (this.formSchema && this.formSchema.sections) || [];
    },

    sectionE() {
      return this.schemaSections.find((section) => section.key === "E") || null;
    },

    // the declaration notice (system, always last in E)
    declarationItem() {
      if (!this.sectionE) return null;
      return this.sectionE.items.find((item) => item.key === "DECLARATION") || null;
    },

    // other items a package placed in E are shown above the declaration
    reviewExtras() {
      if (!this.sectionE) return null;
      const items = this.sectionE.items.filter((item) => item.key !== "DECLARATION");
      return items.length ? { ...this.sectionE, items } : null;
    },

    hasRecat() {
      return this.schemaSections.some((section) => section.items.some((item) => item.builtinKey === "recategorisation"));
    },

    // Wizard steps: one per form section from the schema (E excluded), then Documents, then Review & sign.
    steps() {
      const list = [];
      this.schemaSections.forEach((section) => {
        if (section.key === "E") return;

        if (section.personType) {
          const config = personConfig(section.personType, this.personTypes);
          if (!config) return;
          list.push({
            key: section.key,
            letter: section.key.length === 1 ? section.key : null,
            icon: section.key.length === 1 ? null : "UsersIcon",
            title: section.title || config.plural,
            subtitle: section.subtitle || config.subtitle,
            type: "persons",
            config,
            section,
          });
          return;
        }

        list.push({
          key: section.key,
          letter: section.key.length <= 2 ? section.key : null,
          icon: section.key.length <= 2 ? null : "FileTextIcon",
          title: section.title,
          subtitle: section.subtitle,
          type: "section",
          section,
        });
      });

      list.push({
        key: "DOCUMENTS",
        letter: null,
        icon: "FileTextIcon",
        title: "Documents",
        subtitle: "Documents requested by Swiss Finance Corporation, your uploads and your requests",
        type: "documents",
      });
      list.push({
        key: "E",
        letter: "E",
        icon: null,
        title: "Review & sign",
        subtitle: (this.sectionE && this.sectionE.subtitle) || "Please review, sign and submit your agreement",
        type: "review",
      });
      return list;
    },

    // Where the wizard opens: review step when locked, first new section when reopened, else the start.
    startIndex() {
      const last = this.steps.length - 1;
      if (last < 0) return 0;
      if (this.formLocked) return last;
      if (this.reopened) {
        const firstNew = this.steps.findIndex(
          (step) => (step.type === "section" || step.type === "persons") && !this.sectionReadOnly(step.key)
        );
        if (firstNew !== -1) return firstNew;
        return this.steps.findIndex((step) => step.type === "documents");
      }
      return 0;
    },

    summarySteps() {
      return this.steps.map((step, index) => {
        let state = "pending";
        if (step.type === "documents") {
          state = index < this.maxVisited ? "done" : "pending";
        } else if (step.type === "review") {
          state = this.formLocked ? "signed" : "pending";
        } else if (this.formLocked || (this.reopened && this.signedSections.indexOf(step.key) !== -1)) {
          state = "signed";
        } else if (this.reopened) {
          state = "new";
        } else if (index < this.maxVisited) {
          state = "done";
        }
        return { key: step.key, title: step.title, state };
      });
    },

    reviewChecklist() {
      return this.steps
        .filter((step) => step.type === "section" || step.type === "persons")
        .map((step) => {
          const signedAlready = this.sectionReadOnly(step.key);
          let note = signedAlready ? "signed" : this.formLocked ? "" : "to be signed now";
          if (step.type === "persons") {
            const count = (this.form.persons[step.config.formKey] || []).length;
            note = `${count} ${count === 1 ? step.config.singular.toLowerCase() : step.config.plural.toLowerCase()}${note ? " · " + note : ""}`;
          }
          return {
            key: step.key,
            title: step.title,
            state: signedAlready ? "signed" : "pending",
            icon: signedAlready ? "CheckCircleIcon" : "Edit3Icon",
            note,
          };
        });
    },

    pendingSectionTitles() {
      const pending = (this.summary && this.summary.sectionsPending) || [];
      return pending
        .filter((key) => key !== "E")
        .map((key) => {
          const step = this.steps.find((s) => s.key === key);
          return step ? step.title : key;
        });
    },

    statusChip() {
      return STATUS_CHIPS[this.status] || STATUS_CHIPS.Draft;
    },

    signedDateLabel() {
      return formatDate(this.signedDate);
    },
  },

  created() {
    setToken(this.token);

    // A stale PIN (the link was re-sent, so a new PIN was issued) comes back as 401 from any
    // call: drop it and ask again. A frozen contact comes back as 423 from any call.
    this.pinInterceptor = publicAxios.interceptors.response.use(
      (res) => res,
      (err) => {
        const data = err.response && err.response.data;
        if (err.response && err.response.status === 401 && data && data.info === "pin_required") {
          this.clearStoredPin();
          this.pinRequired = true;
          this.pinError = "Your PIN has changed. Please enter the PIN from the latest email.";
        }
        if (err.response && err.response.status === 423 && data && data.info === "Frozen") {
          this.frozen = true;
        }
        return Promise.reject(err);
      }
    );
  },

  mounted() {
    this.load();
  },

  beforeDestroy() {
    if (this.pinInterceptor !== undefined) {
      publicAxios.interceptors.response.eject(this.pinInterceptor);
    }
  },

  methods: {
    /* ---------- PIN gate ---------- */
    pinStorageKey() {
      return `onboardingPin:${this.token}`;
    },

    storedPin() {
      try {
        return sessionStorage.getItem(this.pinStorageKey()) || "";
      } catch (e) {
        return "";
      }
    },

    rememberPin(pin) {
      try {
        sessionStorage.setItem(this.pinStorageKey(), pin);
      } catch (e) {
        // private mode or storage blocked: the PIN only lives in memory for this page
      }
      publicAxios.defaults.headers.common["X-Onboarding-Pin"] = pin;
    },

    clearStoredPin() {
      try {
        sessionStorage.removeItem(this.pinStorageKey());
      } catch (e) {
        // ignore
      }
      delete publicAxios.defaults.headers.common["X-Onboarding-Pin"];
    },

    load() {
      this.loading = true;
      getStatus()
        .then((res) => {
          const status = res.data;
          this.accountName = status.accountName;
          this.contactName = status.contactName;

          if (status.frozen) {
            this.frozen = true;
            return null;
          }

          const pin = this.storedPin();
          if (pin) {
            this.rememberPin(pin);
          }
          if (status.requiresPin && !pin) {
            this.pinRequired = true;
            return null;
          }

          return this.loadForm();
        })
        .catch((err) => {
          if (!(err.response && err.response.status === 423)) {
            this.notFound = true;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    verifyPin() {
      this.pinError = null;
      this.pinVerifying = true;
      apiVerifyPin(this.pin.trim())
        .then(() => {
          this.rememberPin(this.pin.trim());
          this.pinRequired = false;
          this.pin = "";
          this.loading = true;
          return this.loadForm().finally(() => {
            this.loading = false;
          });
        })
        .catch((err) => {
          const data = (err.response && err.response.data) || {};
          if (err.response && err.response.status === 423) {
            this.pinLockedUntil = data.lockedUntil || true;
            this.pinError = data.message || "Too many incorrect PINs. Please try again later.";
          } else if (err.response && err.response.status === 422) {
            this.pinError =
              data.attemptsLeft !== undefined
                ? `${data.message || "Incorrect PIN"}. ${data.attemptsLeft} attempt(s) left.`
                : data.message || "Incorrect PIN";
          } else if (err.response && err.response.status === 404) {
            this.notFound = true;
          } else if (err.response && err.response.status === 429) {
            this.pinError = "Too many attempts. Please wait a minute and try again.";
          } else {
            this.pinError = "The PIN could not be checked. Please try again.";
          }
        })
        .finally(() => {
          this.pinVerifying = false;
        });
    },

    /* ---------- payload ---------- */
    loadForm() {
      return getOnboarding()
        .then((res) => {
          this.applyPayload(res.data, { keepSignatures: false });
        })
        .catch((err) => {
          const status = err.response && err.response.status;
          // 401 and 423 are handled by the interceptor (PIN card / paused card)
          if (status !== 401 && status !== 423) {
            this.notFound = true;
          }
        });
    },

    // Replaces the page state with a payload returned by any public route. The form is
    // always taken from the server (it writes personIds into person entries); trader
    // signatures drawn locally are carried over by position unless told otherwise.
    applyPayload(data, options) {
      if (!data || !data.formData) return;
      const keepSignatures = !(options && options.keepSignatures === false);

      const previous = this.form;
      const schema = data.formSchema || this.formSchema || { sections: [] };
      const next = normaliseForm(data.formData, schema);
      if (keepSignatures && previous && previous.persons) {
        Object.keys(next.persons).forEach((formKey) => {
          const before = previous.persons[formKey] || [];
          next.persons[formKey].forEach((person, index) => {
            if (before[index] && before[index].signatureImage) {
              person.signatureImage = before[index].signatureImage;
            }
          });
        });
      }
      if (next.sectionA && !next.sectionA.accountName && data.accountName) {
        next.sectionA.accountName = data.accountName;
      }

      this.formSchema = schema;
      // an empty PHP array arrives as [] rather than {}
      this.noticeSignatures = data.noticeSignatures && !Array.isArray(data.noticeSignatures) ? data.noticeSignatures : {};
      this.form = next;
      this.contactName = data.contactName;
      this.accountName = data.accountName;
      this.accountNumber = data.accountNumber;
      this.status = data.status || "Draft";
      this.signed = !!data.signed;
      this.reopened = !!data.reopened;
      this.complete = !!data.complete;
      this.signedDate = data.signedDate || null;
      this.signatureName = data.signatureName || null;
      this.signedSections = data.signedSections || [];
      this.clientType = data.clientType || null;
      this.clientTypeLocked = !!data.clientTypeLocked;
      this.clientCategory = data.clientCategory || null;
      this.formSections = data.formSections || [];
      this.personTypes = data.personTypes || {};
      this.persons = data.persons || [];
      this.requirements = data.requirements || [];
      this.customerRequests = data.customerRequests || [];
      this.documents = data.documents || [];
      this.summary = data.summary || null;

      // Re-mount the wizard when its steps or the lock state change (new sections,
      // just signed, reopened) so start index and tabs are rebuilt.
      const fingerprint = `${this.steps.map((step) => step.key).join(",")}|${this.formLocked}|${this.reopened}`;
      const changed = !this.wizardReady || fingerprint !== this.wizardFingerprint;
      if (this.wizardReady && changed) {
        this.wizardKey += 1;
      }
      this.wizardFingerprint = fingerprint;
      if (changed) {
        this.activeStepIndex = this.startIndex;
        this.maxVisited = this.startIndex;
      }
      this.wizardReady = true;
    },

    /* ---------- sections ---------- */
    hasSection(key) {
      return this.sectionKeys.indexOf(key) !== -1;
    },

    noticeItem(key) {
      for (let i = 0; i < this.schemaSections.length; i++) {
        const item = this.schemaSections[i].items.find((entry) => entry.kind === "Notice" && entry.key === key);
        if (item) return item;
      }
      return null;
    },

    readPath(path) {
      return String(path)
        .split(".")
        .reduce((current, part) => (current && typeof current === "object" ? current[part] : undefined), this.form);
    },

    // Read-only when the whole form is locked, or (reopened) when the section is covered
    // by the existing signature.
    sectionReadOnly(key) {
      if (this.formLocked) return true;
      return this.reopened && this.signedSections.indexOf(key) !== -1;
    },

    setPersons(formKey, list) {
      this.$set(this.form.persons, formKey, list);
    },

    hasNamelessPerson() {
      return Object.keys(this.form.persons || {}).some((formKey) =>
        (this.form.persons[formKey] || []).some((person) => !person.name || !String(person.name).trim())
      );
    },

    // Deep copy for the API; trader signatures only travel with signForm.
    serialiseForm(includeSignatures) {
      const data = JSON.parse(JSON.stringify(this.form));
      if (!includeSignatures) {
        Object.keys(data.persons).forEach((formKey) => {
          data.persons[formKey].forEach((person) => {
            delete person.signatureImage;
          });
        });
      }
      return data;
    },

    /* ---------- wizard ---------- */
    unwrapRef(name) {
      const ref = this.$refs[name];
      if (!ref) return null;
      return Array.isArray(ref) ? ref[0] : ref;
    },

    observerFor(key) {
      return this.unwrapRef("observer_" + key);
    },

    validateStep(key) {
      const observer = this.observerFor(key);
      return observer ? observer.validate() : Promise.resolve(true);
    },

    // Runs before the wizard leaves step `index` (Next / forward click on a step).
    beforeLeave(index) {
      const step = this.steps[index];
      if (!step) return Promise.resolve(true);
      if (step.type === "documents" || step.type === "review") return Promise.resolve(true);
      if (this.sectionReadOnly(step.key)) return Promise.resolve(true);

      this.errorMessage = null;
      return this.validateStep(step.key).then((valid) => {
        if (!valid) {
          this.errorMessage = "Please complete the required fields on this step before continuing.";
          return false;
        }
        this.errorMessage = null;
        return this.autosave("step");
      });
    },

    onStepChange(prevIndex, nextIndex) {
      if (typeof nextIndex !== "number") return;
      this.activeStepIndex = nextIndex;
      this.maxVisited = Math.max(this.maxVisited, nextIndex);
      this.$nextTick(() => {
        const wrap = this.$refs.wizardWrap;
        if (!wrap) return;
        const top = wrap.getBoundingClientRect().top + window.pageYOffset - 16;
        if (window.pageYOffset > top) window.scrollTo({ top, behavior: "smooth" });
      });
    },

    // Step list / summary card click: respects the wizard's own rules (validates when
    // moving forward, only steps already reached are allowed).
    goToStep(index) {
      const wizard = this.$refs.wizard;
      if (!wizard || index === wizard.activeTabIndex) return;
      this.errorMessage = null;
      wizard.navigateToTab(index);
    },

    // Programmatic jump (to the step holding a signing error): no validation.
    jumpToStep(index) {
      const wizard = this.$refs.wizard;
      if (!wizard || index < 0 || index === wizard.activeTabIndex) return;
      wizard.changeTab(wizard.activeTabIndex, index);
    },

    /* ---------- save ---------- */
    // mode: 'manual' (Save progress button, toast), 'step' (leaving a wizard step),
    // 'background' (a person was just named; skip quietly when it cannot be saved yet).
    // Resolves to true when saved (or nothing to save), false on failure.
    autosave(mode) {
      const silent = mode === "step" || mode === "background";
      if (this.formLocked) return Promise.resolve(true);
      if (this.saving && this.savePromise) return this.savePromise;

      // a person without a name cannot be stored; the step validation enforces names on
      // the current step, this guards persons left blank on other steps
      if (silent && this.hasNamelessPerson()) {
        if (mode === "step") {
          this.errorMessage =
            "Every person needs a name before the form can be saved. Please complete or remove the entries without a name.";
        }
        return Promise.resolve(false);
      }

      this.saving = true;
      this.errorMessage = null;
      this.savePromise = apiSaveForm(this.serialiseForm(false))
        .then((res) => {
          this.applyPayload(res.data);
          if (!silent) {
            this.$bvToast.toast("Your progress has been saved. You can return using the same link.", {
              title: "Saved",
              variant: "success",
              solid: true,
            });
          }
          return true;
        })
        .catch((err) => {
          this.errorMessage = errorMessage(err, "Saving failed. Please try again.");
          return false;
        })
        .finally(() => {
          this.saving = false;
          this.savePromise = null;
        });
      return this.savePromise;
    },

    saveProgress() {
      return this.autosave("manual");
    },

    /* ---------- sign ---------- */
    signAndSubmit() {
      if (this.formLocked || this.signing) return;
      this.errorMessage = null;
      this.showAckHints = true;

      const editableSteps = this.steps
        .map((step, index) => ({ step, index }))
        .filter(({ step }) => step.type !== "documents" && (step.type === "review" || !this.sectionReadOnly(step.key)));

      // validate every editable step in order; stop at the first invalid one
      const validateAll = editableSteps.reduce(
        (chain, { step, index }) =>
          chain.then((failedIndex) => {
            if (failedIndex !== null) return failedIndex;
            return this.validateStep(step.key).then((valid) => (valid ? null : index));
          }),
        Promise.resolve(null)
      );

      validateAll.then((failedIndex) => {
        if (failedIndex !== null) {
          this.errorMessage = `Please complete all required fields in "${this.steps[failedIndex].title}" before signing.`;
          this.jumpToStep(failedIndex);
          return;
        }

        const stepIndex = (key) => this.steps.findIndex((step) => step.key === key);

        // signatures drawn for Signature-mode notices, from every mounted section
        const noticeSignatures = {};
        this.steps.forEach((step) => {
          if (step.type !== "section") return;
          const section = this.unwrapRef("section_" + step.key);
          if (section && section.noticeSignaturesDrawn) Object.assign(noticeSignatures, section.noticeSignaturesDrawn());
        });
        const extras = this.unwrapRef("section_E");
        if (extras && extras.noticeSignaturesDrawn) Object.assign(noticeSignatures, extras.noticeSignaturesDrawn());

        // the same checks the server runs, for immediate feedback
        const editableKeys = this.sectionKeys.filter((key) => !this.sectionReadOnly(key) || !this.reopened);
        const problem = validateForSign(this.formSchema, this.form, editableKeys, noticeSignatures, this.noticeSignatures);
        if (problem) {
          this.errorMessage = problem.message;
          const index = problem.sectionKey === "E" ? this.steps.length - 1 : stepIndex(problem.sectionKey);
          if (index >= 0) this.jumpToStep(index);
          return;
        }

        const review = this.unwrapRef("review");
        const signatureImage = review ? review.declarationSignature() : null;
        if (!signatureImage) {
          this.errorMessage = "Please draw your signature in the Client Declaration.";
          return;
        }

        // person signatures live in form.persons.<type>[i].signatureImage (kept up to date
        // by the signature pads); refresh them from the pads in case the step is mounted
        const formData = this.serialiseForm(true);
        this.steps
          .filter((step) => step.type === "persons")
          .forEach((step) => {
            const list = this.unwrapRef("persons_" + step.key);
            const signatures = list ? list.collectSignatures() : [];
            (formData.persons[step.config.formKey] || []).forEach((person, index) => {
              if (signatures[index]) person.signatureImage = signatures[index];
            });
          });

        const payload = {
          formData,
          signatureName: this.form.sectionE.name,
          signatureImage,
          noticeSignatures: Object.keys(noticeSignatures).reduce((out, key) => {
            const ack = (this.form.notices && this.form.notices[key]) || {};
            const item = this.noticeItem(key);
            const stored = item ? this.readPath(item.dataKey) : null;
            out[key] = { image: noticeSignatures[key], name: (stored && stored.name) || ack.name || null };
            return out;
          }, {}),
          recatSignatureName: this.hasRecat && this.form.sectionF ? this.form.sectionF.name || null : null,
          recatSignatureImage: this.hasRecat && review ? review.recatSignature() : null,
        };

        this.signing = true;
        apiSignForm(payload)
          .then((res) => {
            this.applyPayload(res.data, { keepSignatures: false });
            this.$bvToast.toast(res.data.message || "Thank you. The form has been signed and submitted.", {
              title: "Signed",
              variant: "success",
              solid: true,
            });
            this.showAckHints = false;
            window.scrollTo({ top: 0, behavior: "smooth" });
          })
          .catch((err) => {
            this.errorMessage = errorMessage(err, "Signing failed. Please try again.");
            const data = err.response && err.response.data;
            if (data && data.sectionKey) {
              const index = data.sectionKey === "E" ? this.steps.length - 1 : stepIndex(data.sectionKey);
              if (index >= 0) this.jumpToStep(index);
            }
          })
          .finally(() => {
            this.signing = false;
          });
      });
    },
  },
};
</script>

<style lang="scss">
@import "./clientOnboarding.scss";
</style>

